import React, { useState, useEffect } from 'react';
import {
  Sparkles, ChevronDown, ChevronUp, AlertTriangle, Package, Truck,
  MapPin, ChevronRight, CheckCircle2, Lightbulb
} from 'lucide-react';
import '../styles/ai-tracking.css';

const AIOrderTracking = ({ orderId, orderStatus }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (orderId && orderStatus && !tracking) {
      fetchTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderStatus]);

  const fetchTracking = async () => {
    if (tracking && !loading) {
      setExpanded(prev => !prev);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`/api/customer/order-tracking/${orderId}/track-with-ai`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTracking(data);
        setExpanded(true);
      } else {
        throw new Error('Failed to fetch tracking');
      }
    } catch (err) {
      setError('Unable to load tracking details');
    } finally {
      setLoading(false);
    }
  };

  const insights = tracking?.aiInsights;
  const timeline = tracking?.timeline || [];

  return (
    <div className="aitrack">
      <button
        type="button"
        className="aitrack__toggle"
        onClick={fetchTracking}
        disabled={loading}
        aria-expanded={expanded}
      >
        <span className="aitrack__toggle-icon" aria-hidden>
          <Sparkles size={16} />
        </span>
        <span className="aitrack__toggle-text">
          {loading
            ? 'Loading tracking…'
            : tracking
              ? (expanded ? 'Hide tracking details' : 'Show tracking details')
              : 'View AI tracking details'}
        </span>
        {tracking && !loading && (
          <span className="aitrack__chev" aria-hidden>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        )}
      </button>

      {error && (
        <div className="aitrack__error" role="alert">
          <AlertTriangle size={14} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {expanded && tracking && (
        <div className="aitrack__content">
          {timeline.length > 0 && (
            <section className="aitrack__section">
              <h4 className="aitrack__section-title">
                <Package size={14} aria-hidden />
                Delivery progress
              </h4>
              <ol className="aitrack__timeline">
                {timeline.map((event, index) => {
                  const isCurrent = index === timeline.length - 1;
                  return (
                    <li
                      key={index}
                      className={`aitrack__step ${isCurrent ? 'is-current' : 'is-done'}`}
                    >
                      <span className="aitrack__step-marker" aria-hidden>
                        <span className={`aitrack__dot ${isCurrent ? 'is-pulse' : ''}`} />
                      </span>
                      <div className="aitrack__step-body">
                        <div className="aitrack__step-status">{event.status}</div>
                        {event.description && (
                          <div className="aitrack__step-desc">{event.description}</div>
                        )}
                        {event.timestamp && (
                          <div className="aitrack__step-time">
                            {new Date(event.timestamp).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {insights && (
            <section className="aitrack__section">
              <h4 className="aitrack__section-title">
                <Sparkles size={14} aria-hidden />
                AI insights
              </h4>
              <div className="aitrack__insights">
                {insights.estimatedDelivery && (
                  <div className="aitrack__insight aitrack__insight--featured">
                    <div className="aitrack__insight-head">
                      <Truck size={14} aria-hidden />
                      <span>Estimated delivery</span>
                    </div>
                    <p className="aitrack__insight-text aitrack__insight-text--lg">
                      {insights.estimatedDelivery}
                    </p>
                  </div>
                )}

                <div className="aitrack__insight-grid">
                  {insights.statusExplanation && (
                    <div className="aitrack__insight">
                      <div className="aitrack__insight-head">
                        <MapPin size={14} aria-hidden />
                        <span>Current status</span>
                      </div>
                      <p className="aitrack__insight-text">{insights.statusExplanation}</p>
                    </div>
                  )}
                  {insights.nextSteps && (
                    <div className="aitrack__insight">
                      <div className="aitrack__insight-head">
                        <ChevronRight size={14} aria-hidden />
                        <span>What's next</span>
                      </div>
                      <p className="aitrack__insight-text">{insights.nextSteps}</p>
                    </div>
                  )}
                </div>

                {insights.customerActions && (
                  <div className="aitrack__insight aitrack__insight--action">
                    <div className="aitrack__insight-head">
                      <CheckCircle2 size={14} aria-hidden />
                      <span>Action required</span>
                    </div>
                    <p className="aitrack__insight-text">{insights.customerActions}</p>
                  </div>
                )}

                {insights.trackingTips && (
                  <div className="aitrack__insight aitrack__insight--tip">
                    <div className="aitrack__insight-head">
                      <Lightbulb size={14} aria-hidden />
                      <span>Helpful tip</span>
                    </div>
                    <p className="aitrack__insight-text">{insights.trackingTips}</p>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default AIOrderTracking;
